<template>
  <div class="w-full h-full mt-8">
    <div class="mb-4">
      <!-- Informational Alert -->
        <div class="mb-6 bg-[#FFF9E6] border border-[#FFE5B4] rounded-lg p-4 flex items-start gap-3" style="background-color: #FFF9E6; border-color: #FFE5B4; border-radius: 8px; padding: 12px; display: flex; align-items: center; gap: 8px;">
          <Icon icon-name="bulb-alert" class="flex-shrink-0" style="fill: transparent; width: 24px; height: 24px;" />
          <p class="text-sm text-[#E99323] leading-relaxed">
            NHIS (National Health Insurance Scheme) is a government health insurance program providing affordable healthcare through approved HMOs. Employers contribute 10% and employees contribute 5% of the basic salary.
          </p>
        </div>

        <!-- Enable NHIS Toggle -->
        <div class="flex items-center gap-3 mb-6" v-if="enableOrDisableNhis">
          <label class="switch">
            <input
              type="checkbox"
              :checked="nhisEnabled"
              @change="handleNhisToggle($event.target.checked)"
            />
            <span class="slider round"></span>
          </label>
          <p class="text-sm font-semibold text-gray-700">Enable NHIS for your employees</p>
        </div>
        <p v-else class="font-extrabold mb-8">
          {{
            nhisEnabled
              ? "NHIS Enabled"
              : "NHIS Disabled"
          }}
          <small
            v-if="!enableOrDisableNhis"
            class="text-flame block text-sm font-bold"
            >You do not have permission to enable or disable
            NHIS</small
          >
        </p>
        <div class="flex gap-6 mb-6">
          <div class="w-1/3">
            <label class="text-xs font-bold">NHIA Employer Code</label>
            <c-text
              v-model="nhiaEmployerCode"
              class="form-input w-full"
              :disabled="!nhisEnabled || !enableOrDisableNhis"
              placeholder="--Enter--"
            />
          </div>
          <div class="w-1/3">
            <label class="text-xs font-bold">Employee Contribution</label>
            <div class="flex items-center">
              <c-text
                v-model="employeeContribution"
                class="form-input w-full"
                type="number"
                placeholder="--Enter--"
                min="0"
                max="100"
                disabled
              />
              <span class="ml-2 font-bold">%</span>
            </div>
          </div>
          <div class="w-1/3">
            <label class="text-xs font-bold">Organisation Contribution</label>
            <div class="flex items-center">
              <c-text
                v-model="organisationContribution"
                class="form-input w-full"
                type="number"
                placeholder="--Enter--"
                min="0"
                max="100"
                disabled
              />
              <span class="ml-2 font-bold">%</span>
            </div>
          </div>
        </div>

      <Card class="p-8">
        <div class="flex items-center gap-2 ">
          <div class="font-black text-l font-bold ">HEALTH MAINTENANCE ORGANISATIONS</div>
          <div @click="openHmoSidebar" class="flex item-center text-dynamicBackBtn font-bold cursor-pointer gap-2 p-0 ml-auto">
            <Icon icon-name="icon-plus" size="xs" class="text-blueCrayola icon icon-plus"></Icon>
            <span class="mr-1">ADD NEW</span>
          </div>
        </div>

        <sTable
          :headers="hmoHeaders"
          :items="hmoTableData"
          aria-label="HMO table"
          class="w-full"
          :has-checkbox="false"
          :has-number="false"
          :loading="false"
        >
          <template v-slot:item="{ item }">
            <span v-if="item.name" class="text-m font-medium text-gray-700">
              {{ item.data.name.data }}
            </span>
            <span v-if="item.actions" class="flex items-center">
              <div
                @click="editHmo(item.data.original)"
                class="cursor-pointer"
              >
                <Icon icon-name="edit" size="s" class="text-blueCrayola icon icon-edit"></Icon>
              </div>
            </span>
          </template>
        </sTable>
      </Card>

      <div class="flex gap-4 mt-20">
        <Button :class="`bg-dynamicBackBtn text-white`" :disabled="!enableOrDisableNhis" @click="saveNhisSettings" >
          Save Changes
        </Button>
        <Button class="text-gray-600 bg-white border border-gray-300" @click="revertNhisSettings" :disabled="!nhisEnabled || !enableOrDisableNhis">
          Revert Changes
        </Button>
      </div>
    </div>
    <!-- Add HMO Modal (skeleton) -->
    <RightSideBar
      v-if="hmoAddSidebarOpen"
      :loading="hmoLoading"
      :submit="'Save'"
      :cancel="'Cancel'"
      @close="closeHmoSidebar"
      @submit="saveSelectedHmos"
      :show-footer="true"
      button-class="bg-dynamicBackBtn text-white"

    >
      <template #title>
        <div class="font-bold text-lg text-darkPurple">
          {{ isEditMode ? 'Edit Health Maintenance Organisation' : 'Add a New Health Maintenance Organisation' }}
        </div>
      </template>
      <div class="mb-4">
        <p class="text-s text-darkPurple mb-5 border-b border-gray-400 pb-3">Search or select HMOs to make available to your employees.</p>
        <label class="block text-s font-bold mb-2 text-darkPurple mt-5">Health Maintenance Organisations (HMOs)</label>

        <!-- Custom Multi-Select Dropdown -->
        <div class="relative" ref="multiSelectDropdown">
          <!-- Input Field -->
          <div
            class="form-input w-full border border-gray-300 rounded-md px-3 py-2 min-h-[40px] flex items-center cursor-pointer bg-white"
            :class="{ 'border-blue-500': isDropdownOpen }"
            @click="toggleDropdown"
          >
            <div class="flex-1">
              <span class="text-gray-400">--Search or select--</span>
            </div>
            <Icon icon-name="caret-down" size="xs" class="ml-2 text-gray-500" />
          </div>

          <!-- Dropdown Menu -->
          <div
            v-if="isDropdownOpen"
            class="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-60 overflow-hidden flex flex-col"
          >
            <!-- Search Bar -->
            <div class="relative px-3 py-2 border-b border-gray-200">
              <Icon icon-name="icon-search" size="xs" class="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                v-model="searchQuery"
                type="text"
                class="w-full pl-8 pr-8 py-1 border-0 focus:outline-none text-sm"
                placeholder="Search here"
                @click.stop
                @input="filterHmos"
              />
              <button
                v-if="searchQuery"
                @click.stop="clearSearch"
                class="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <Icon icon-name="icon-close" size="xs" />
              </button>
            </div>

            <!-- Options List -->
            <div class="overflow-y-auto max-h-48">
              <div
                v-for="hmo in filteredHmos"
                :key="hmo.id"
                class="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                :class="{ 'bg-gray-50': isHmoSelected(hmo.id), 'opacity-50': isHmoDisabled(hmo.id) }"
                @click.stop="toggleHmoSelection(hmo.id)"
              >
                <Checkbox
                  v-model="hmo.checked"
                  checkbox-size="height:16px; width:16px; margin-top: 2px;"
                  :disabled="isHmoDisabled(hmo.id)"
                  @change="toggleHmoSelection(hmo.id)"
                />
                <span class="text-sm flex-1">{{ hmo.name }}</span>
              </div>
              <div v-if="filteredHmos.length === 0" class="px-4 py-3 text-sm text-gray-500 text-center">
                No HMOs found
              </div>
            </div>
          </div>
        </div>

        <!-- Selected Tags Display (Outside dropdown) -->
        <div v-if="selectedHmos.length > 0" class="flex flex-wrap gap-2 mt-3">
          <span
            v-for="hmo in selectedHmos"
            :key="hmo.id"
            class="bg-[#F7F7F7] px-3 py-1.5 rounded-md text-xs flex items-center gap-2" style="background-color: #F7F7F7;"
          >
            {{ hmo.name }}
            <Icon
              icon-name="icon-close"
              size="xs"
              class="cursor-pointer text-darkPurple hover:text-gray-700 ml-1 rounded-full border border-darkPurple"
              @click.native="removeSelectedHmo(hmo.id)"
            />
          </span>
        </div>
      </div>
    </RightSideBar>
  </div>
</template>

<script>
import Card from "@/components/Card";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import RightSideBar from '@/components/RightSideBar';
import CText from "@scelloo/cloudenly-ui/src/components/text";
import STable from "@scelloo/cloudenly-ui/src/components/table";
import Checkbox from "@/components/Checkbox";

export default {
  components: {
    Card,
    Button,
    Icon,
    RightSideBar,
    CText,
    STable,
    Checkbox,
  },
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      enableOrDisableNhis: false,
      nhisEnabled: false,
      nhiaEmployerCode: null,
      employeeContribution: 5,
      organisationContribution: 10,
      orgPaySettingId: this.$store.state.paySettingsActiveIds.NHIS || null,
      nhisPaySettingId: this.$store.state.paySettingsActiveIds.NHIS || null,
      orgHmos: [],
      showAddHmoModal: false,
      hmoAddSidebarOpen: false,
      allHmos: [],
      selectedHmos: [],
      hmoLoading: false,
      isDropdownOpen: false,
      searchQuery: '',
      filteredHmos: [],
      isEditMode: false,
      editingHmo: null,
      hmoHeaders: [
        { title: "HEALTH MAINTENANCE ORGANISATIONS", value: "name" },
        { title: "", value: "actions" },
      ],
    };
  },
  computed: {
    hmoTableData() {
      if (!this.orgHmos || !Array.isArray(this.orgHmos)) {
        return [];
      }
      return this.orgHmos.map((hmo) => ({
        name: { data: hmo.hmoName || hmo.title || '' },
        actions: { data: { original: hmo } },
      }));
    },
  },
  methods: {
    async enableOrDisableNhisPrivilege() {
      try {
        await this.$handlePrivilege("paySettings", "enableOrDisableNhis");
        this.enableOrDisableNhis = true;
      } catch (error) {
        this.enableOrDisableNhis = false;
      }
    },
    handleNhisToggle(enabled) {
      this.nhisEnabled = enabled;
      if (!enabled) {
        this.nhiaEmployerCode = null;
      }
    },
    async saveNhisSettings() {
      try {
        await this.$_saveNhisSettings({
          orgId: this.$orgId,
          orgPaySettingId: this.$store.state.paySettingsActiveIds.ACTIVE || null,
          employerCode: this.nhiaEmployerCode,
         orgContribution: this.organisationContribution,
         employeeContribution: this.employeeContribution,
         employeesCanChooseNhis: this.nhisEnabled,
        });
        this.$toasted.success("Settings saved", {
          duration: 5000,
        });
      } catch (error) {
        const errorMessage = error.response?.data?.message ||
          (error.response?.data?.errors?.[0] && Object.values(error.response.data.errors[0])[0]) ||
          (error.errors?.[0] && Object.values(error.errors[0])[0]) ||
          'Failed to save settings';

        this.$toasted.error(errorMessage, {
          duration: 5000,
        });
      }
    },
    async revertNhisSettings() {
      try {
        const { data } = await this.$_getNhisSettings({ orgPaySettingId: this.$store.state.paySettingsActiveIds.ACTIVE  || ''});
        const setting = data?.setting || data || {};
        this.nhisEnabled = setting.employeesCanChooseNhis ?? this.nhisEnabled;
        this.nhiaEmployerCode = setting.employerCode || setting.nhiaEmployerCode || "";
        this.$toasted.success("Settings reverted", {
          duration: 5000,
        });
      } catch (error) {
        this.$toasted.error("Settings not reverted", {
          duration: 5000,
        });
      }
    },
    getNhisSettings() {
      this.$_getNhisSettings({ orgPaySettingId: this.$store.state.paySettingsActiveIds.ACTIVE || '' })
        .then(({ data }) => {
          const setting = data?.nhis || data || {};
          this.nhiaEmployerCode = setting.employerCode || setting.nhiaEmployerCode || "";
          this.nhisEnabled = setting.employeesCanChooseNhis;
        })
        .catch(() => {});
    },
    async getOrgHmos() {
      const { data } = await this.$_getOrgHmos();
      this.orgHmos = data.payload;
    },
    async addOrgHmo(hmo) {
      try {
        await this.$_addOrgHmo(hmo.id);
        this.$toasted.success("HMO added", {
          duration: 5000,
        });
        this.showAddHmoModal = false;
        this.getOrgHmos();
      } catch (error) {
        this.$toasted.error("HMO not added", {
          duration: 5000,
        });
      }
    },
    async editHmo(hmo) {
      try {
        await this.$handlePrivilege("paySettings", "addHmo");
        this.isEditMode = true;
      this.editingHmo = hmo;
      this.hmoAddSidebarOpen = true;
      this.hmoLoading = true;

      this.selectedHmos = this.orgHmos.map((h) =>
        ({
          id: h.hmoId,
          name: h.hmoName
        })
      ) || [];

      this.$_getHmoList()
        .then(({ data }) => {
          this.allHmos = (data || []).map(hmoItem => ({
            ...hmoItem,
            checked: this.selectedHmos.some(selectedHmo => selectedHmo.id === hmoItem.id)
          }));
          this.filteredHmos = this.allHmos;
        })
        .finally(() => { this.hmoLoading = false; });
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async openHmoSidebar() {
      try {
        await this.$handlePrivilege("paySettings", "addHmo");

        this.isEditMode = false;
      this.editingHmo = null;
      this.hmoAddSidebarOpen = true;
      this.hmoLoading = true;

      // Prefill selectedHmos with existing orgHmos
      this.selectedHmos = this.orgHmos.map((h) => ({
        id: h.hmoId,
        name: h.hmoName
      })) || [];

      this.$_getHmoList()
        .then(({ data }) => {
          // fallback to common shapes
          this.allHmos = (data || []).map(hmo => ({
            ...hmo,
            checked: this.selectedHmos.some(selectedHmo => selectedHmo.id === hmo.id)
          }));
          this.filteredHmos = this.allHmos;
        })
        .finally(() => { this.hmoLoading = false; });
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    toggleDropdown() {
      if (this.hmoLoading) return;
      this.isDropdownOpen = !this.isDropdownOpen;
      if (this.isDropdownOpen && this.filteredHmos.length === 0) {
        this.filteredHmos = [...this.allHmos];
      }
    },
    filterHmos() {
      if (!this.searchQuery.trim()) {
        this.filteredHmos = [...this.allHmos];
        return;
      }
      const query = this.searchQuery.toLowerCase();
      this.filteredHmos = this.allHmos.filter(hmo =>
        hmo.name.toLowerCase().includes(query)
      );
    },
    clearSearch() {
      this.searchQuery = '';
      this.filteredHmos = [...this.allHmos];
      this.allHmos.forEach((hmo, index) => {
        this.$set(this.allHmos[index], 'checked', this.selectedHmos.some(selectedHmo => selectedHmo.id === hmo.id));
      });
    },
    isHmoSelected(hmoId) {
      return this.selectedHmos.some(selectedHmo => selectedHmo.id === hmoId);
    },
    isHmoDisabled(hmoId) {
      return this.orgHmos?.some(h => h.id === hmoId) || false;
    },
    toggleHmoSelection(hmoId) {
      if (this.isHmoDisabled(hmoId)) return;

      const hmoIndex = this.allHmos.findIndex(h => h.id === hmoId);
      if (hmoIndex === -1) return;

      const index = this.selectedHmos.findIndex(selectedHmo => selectedHmo.id === hmoId);
      if (index > -1) {
        this.selectedHmos.splice(index, 1);
        this.$set(this.allHmos[hmoIndex], 'checked', false);
      } else {
        this.selectedHmos.push({ id: hmoId, name: this.allHmos[hmoIndex].name });
        this.$set(this.allHmos[hmoIndex], 'checked', true);
      }
    },
    closeHmoSidebar() {
      this.hmoAddSidebarOpen = false;
      this.selectedHmos = [];
      this.isDropdownOpen = false;
      this.searchQuery = '';
      this.filteredHmos = [];
      this.isEditMode = false;
      this.editingHmo = null;
    },
    removeSelectedHmo(hmoId) {
      this.selectedHmos = this.selectedHmos.filter(selectedHmo => selectedHmo.id !== hmoId);
      const hmoIndex = this.allHmos.findIndex(h => h.id === hmoId);
      if (hmoIndex !== -1) {
        this.$set(this.allHmos[hmoIndex], 'checked', false);
      }
    },
    hmoNameById(id) {
      const hmo = this.allHmos.find(h => h.id === id);
      return hmo ? hmo.hmoName : '';
    },
    async saveSelectedHmos() {
      this.hmoLoading = true;
      try {
        const selectedHmoIds = this.selectedHmos?.map(hmo => hmo.id).filter(id => id) || [];

        // Validate that all IDs are valid UUIDs
        if (selectedHmoIds.length === 0) {
          this.$toasted.error('Please select at least one HMO', { duration: 5000 });
          this.hmoLoading = false;
          return;
        }

        // Ensure all IDs are valid (not undefined/null)
        const validHmoIds = selectedHmoIds.filter(id => id && typeof id === 'string');

        if (validHmoIds.length === 0) {
          this.$toasted.error('Invalid HMO selection', { duration: 5000 });
          this.hmoLoading = false;
          return;
        }

        this.$_addOrgHmoApi(validHmoIds).then(() => {
          this.$toasted.success('HMO(s) saved successfully', { duration: 5000 });
          this.closeHmoSidebar();
          this.getOrgHmos();
        }).catch((error) => {
          this.$toasted.error(error.message || 'Failed to save HMOs', { duration: 5000 });
        }).finally(() => {
          this.hmoLoading = false;
        });

      } catch (error) {
        this.$toasted.error(error.message || 'Failed to save HMOs', { duration: 5000 });
      } finally {
        this.hmoLoading = false;
      }
    },
    async $_fetchAllHmos() {
      // fetches HMO list from /hr/hmo/list
      const { data } = await this.$_getHmoList();
      return (data && (data.hmos || data.data || data)) || [];
    },
    async $_addOrgHmoApi(hmoIds) {
      // Accepts either single ID or array of IDs
      await this.$_addOrgHmo(hmoIds);
    },
    handleClickOutside(event) {
      if (this.$refs.multiSelectDropdown && !this.$refs.multiSelectDropdown.contains(event.target)) {
        this.isDropdownOpen = false;
      }
    },
  },
  watch: {
    isActive(newVal) {
      if (newVal) {
        this.getNhisSettings();
        this.getOrgHmos();
      }
    },
  },
  async mounted() {
    this.enableOrDisableNhisPrivilege();
    if (this.isActive) {
      this.getNhisSettings();
      this.getOrgHmos();
    }
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style scoped>
.switch {
    position: relative;
    display: inline-block;
    width: 24px;
    height: 12px;
  }

  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #EEEEEE;
    transition: 0.4s;
    border-radius: 20px;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 9.6px;
    width: 9.6px;
    border-radius: 50%;
    left: 2px;
    bottom: 1.5px;
    background-color: #878E99;
    transition: 0.4s;
  }

  input:checked + .slider {
    background-color: #EEEEEE;
  }
  input:checked+.slider:before {
    transform: translateX(10px);
    background-color:#13B56A;
  }

  .switch-header {
    font-size: 10px;
    text-transform: uppercase;
    font-weight: 600;
    color: #878E99;
  }
</style>
